import { isValidEmail } from './contact'

/**
 * Klanten zonder e-mailadres en dubbele dossiers (BEHEER-PLAN §3.3, de overstap uit Salonized).
 * Een klant is te bereiken via e-mail óf telefoon; minstens één van de twee is verplicht.
 * Dezelfde regels gebruikt de server (LARAVEL.md, `POST /admin/customers`). Puur en getest.
 */

export type CustomerContact = { firstName: string; lastName: string; email: string; phone: string }

/** "06-12 34 56 78", "+31 6 12345678" en "0031612345678" → "612345678": vergelijkbaar, niet om te bellen. */
export function normalizePhone(phone: string): string {
  let digits = phone.replace(/[^\d+]/g, '')
  if (digits.startsWith('+')) digits = digits.slice(1)
  else if (digits.startsWith('00')) digits = digits.slice(2)
  // Landcode NL of BE eraf, en de voorloopnul: dan is 06… gelijk aan +316….
  if (digits.startsWith('31') || digits.startsWith('32')) digits = digits.slice(2)
  return digits.replace(/^0+/, '')
}

const norm = (s: string) =>
  s
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')

export type CreateProblem = { field: 'firstName' | 'contact' | 'email' | 'phone'; message: string }

/** Wat er ontbreekt of niet klopt bij een nieuwe klant. `null` = in orde. */
export function customerCreateProblem(c: CustomerContact): CreateProblem | null {
  if (!c.firstName.trim()) return { field: 'firstName', message: 'Vul een voornaam in.' }
  const email = c.email.trim()
  const phone = normalizePhone(c.phone)
  if (!email && !phone) return { field: 'contact', message: 'Vul een telefoonnummer of e-mailadres in, zodat je haar kunt bereiken.' }
  if (email && !isValidEmail(email)) return { field: 'email', message: 'Dit e-mailadres klopt niet.' }
  if (c.phone.trim() && phone.length < 8) return { field: 'phone', message: 'Dit telefoonnummer is te kort.' }
  return null
}

/**
 * Mag het beheer het e-mailadres van deze klant wijzigen? Wel toevoegen of wijzigen zolang ze geen
 * login heeft. Met een login niet: dat adres is haar inlog, en een tikfout zou haar account aan
 * iemand anders geven. Dat wijzigt ze zelf in Mijn omgeving (met bevestigingsmail).
 */
export const emailEditable = (c: { email: string; hasPassword: boolean; emailVerified: boolean }) => !(c.email.trim() && (c.hasPassword || c.emailVerified))

export type DuplicateReason = 'email' | 'phone' | 'name'
export type Duplicate<T> = { customer: T; reasons: DuplicateReason[] }

/**
 * Mogelijke dubbelen: zelfde e-mail, zelfde telefoon of precies dezelfde voor- en achternaam.
 * Sterkste overeenkomst eerst (e-mail, dan telefoon, dan naam). Alleen een waarschuwing: twee
 * mensen kunnen dezelfde naam hebben, en een moeder en dochter hetzelfde vaste nummer.
 */
export function duplicateCandidates<T extends { id: string; firstName: string; lastName: string; email: string; phone?: string }>(
  list: readonly T[],
  c: Partial<CustomerContact>,
  { exceptId }: { exceptId?: string } = {},
): Duplicate<T>[] {
  const email = norm(c.email ?? '')
  const phone = normalizePhone(c.phone ?? '')
  const name = c.lastName?.trim() ? `${norm(c.firstName ?? '')} ${norm(c.lastName)}` : ''
  const rank: Record<DuplicateReason, number> = { email: 0, phone: 1, name: 2 }
  return list
    .filter((x) => x.id !== exceptId)
    .map((x) => {
      const reasons: DuplicateReason[] = []
      if (email && norm(x.email) === email) reasons.push('email')
      if (phone.length >= 8 && x.phone && normalizePhone(x.phone) === phone) reasons.push('phone')
      if (name && `${norm(x.firstName)} ${norm(x.lastName)}` === name) reasons.push('name')
      return { customer: x, reasons }
    })
    .filter((d) => d.reasons.length > 0)
    .sort((a, b) => rank[a.reasons[0]] - rank[b.reasons[0]] || b.reasons.length - a.reasons.length)
}

export const duplicateReasonLabel: Record<DuplicateReason, string> = {
  email: 'zelfde e-mailadres',
  phone: 'zelfde telefoonnummer',
  name: 'zelfde naam',
}
